/**
 * E-Mail-Link-Sicherheit: Links in den (sandboxed) E-Mail-iframes klickbar machen
 * und IMMER sicher in einem neuen Tab öffnen.
 *
 * Hintergrund: Der E-Mail-Inhalt wird in einem <iframe sandbox="allow-same-origin">
 * gerendert. Diese Sandbox blockiert jede Navigation — Links/Buttons in Mails
 * (z. B. Wise "Approve new device") reagierten daher nicht.
 *
 * Lösung (ohne Skripte im iframe zu erlauben → bleibt sicher):
 * Da der iframe same-origin ist, kann das übergeordnete Fenster Klicks im iframe
 * abfangen und den Link selbst via window.open('_blank', 'noopener,noreferrer')
 * in einem neuen Tab öffnen. So bleibt der Sandbox-Schutz erhalten (kein
 * allow-scripts), und Links funktionieren trotzdem zuverlässig.
 *
 * Robust: ein periodischer Re-Scan kümmert sich um neu gerenderte iframes und um
 * gewechselte Mails (der iframe-Inhalt wird per document.write neu geschrieben).
 */

const PATCH_FLAG = '__emailLinkSafetyPatched';

function handleIframeClicks(doc: Document) {
  doc.addEventListener(
    'click',
    (e: MouseEvent) => {
      const path = e.composedPath ? e.composedPath() : [];
      let anchor: HTMLAnchorElement | null = null;
      for (const node of path) {
        if (node instanceof HTMLAnchorElement) { anchor = node; break; }
      }
      if (!anchor) {
        const t = e.target as HTMLElement | null;
        anchor = (t && t.closest ? (t.closest('a') as HTMLAnchorElement | null) : null);
      }
      if (!anchor) return;

      const href = anchor.getAttribute('href') || '';
      if (!href) return;
      // In-Page-Anker und potenziell gefährliche Schemata ignorieren
      const lower = href.trim().toLowerCase();
      if (lower.startsWith('#') || lower.startsWith('javascript:') || lower.startsWith('data:')) return;

      // Navigation im Sandbox-iframe verhindern und sicher im neuen Tab öffnen
      e.preventDefault();
      e.stopPropagation();
      window.open(href, '_blank', 'noopener,noreferrer');
    },
    true, // Capture-Phase, damit es zuverlässig greift
  );
}

function scanAndPatch() {
  const iframes = document.querySelectorAll('iframe[title="E-Mail Inhalt"]');
  iframes.forEach((el) => {
    const iframe = el as HTMLIFrameElement;
    let doc: Document | null = null;
    try {
      doc = iframe.contentDocument; // same-origin → erlaubt
    } catch {
      return; // cross-origin: nichts tun
    }
    if (!doc) return;
    const flagged = doc as unknown as Record<string, unknown>;
    if (flagged[PATCH_FLAG]) return;
    flagged[PATCH_FLAG] = true;
    handleIframeClicks(doc);
  });
}

let installed = false;

export function installEmailLinkSafety(): void {
  if (typeof window === 'undefined' || installed) return;
  installed = true;
  // Sofort + periodisch (deckt neue iframes und gewechselte Mails ab)
  const run = () => { try { scanAndPatch(); } catch { /* defensiv */ } };
  run();
  window.setInterval(run, 500);
}
